<template>
  <section class="row">
    <div :class="[$style.container, 'col-md-10 col-lg-8']">
      <HgCardFlat>
        <h4 :class="$style.heading">{{ $t('localLanding.anchorLinks.title') }}</h4>
        <ul>
          <li v-for="linkItem in links" :key="linkItem.anchorId">
            <HgLink :href="`#${linkItem.anchorId}`" @click="handleClick(linkItem.anchorId)">
              {{
                $t(linkItem.text, {
                  propertyType: $t(`localLanding.propertyType.${propertyType}`),
                  location: routeLocationName,
                })
              }}
            </HgLink>
          </li>
        </ul>
      </HgCardFlat>
    </div>
  </section>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { getLocationInfos } from '@/helpers/GeoLocation/GeoLocationHelper';
import { useLocalLandingStore } from '@/stores/localLanding/localLanding';
import { trackingGenericClickEvent } from '@/tracking/tracking';
import {
  LOCAL_LANDING_PAGE_ACTION,
  LOCAL_LANDING_PAGE_CATEGORY,
  LOCAL_LANDING_PAGE_EVENT,
} from '@/tracking/types';
import { AnchorLinks } from '@/types';
import { HgCardFlat, HgLink, HgSupportedLanguage } from '@smg-real-estate/domus';

const localLandingStore = useLocalLandingStore();
const { locale } = useI18n();
const { propertyType, routeGeoLocation } = storeToRefs(localLandingStore);

const isArticleVisible = computed(() => {
  return locale.value !== HgSupportedLanguage.EN;
});

const routeLocationName = computed((): string => {
  return getLocationInfos(routeGeoLocation.value, locale.value as HgSupportedLanguage).name;
});

const links = computed(() => {
  return [
    {
      anchorId: AnchorLinks.PriceDevelopment,
      text: 'localLanding.priceDevelopment.title',
    },
    {
      anchorId: AnchorLinks.HowItWorks,
      text: 'landing.howitworks.title',
    },
    {
      anchorId: AnchorLinks.LocalContent,
      text: 'localLanding.localContent.sectionHeading',
    },
    {
      anchorId: AnchorLinks.PropertyValuation,
      text: 'landing.cta2.title',
    },
    {
      anchorId: AnchorLinks.SellWithAgent,
      text: 'localLanding.sellingPortal.title',
    },
    {
      anchorId: AnchorLinks.MarketAtlas,
      text: 'landingPage.marketAtlas.title',
    },
    {
      anchorId: AnchorLinks.FAQ,
      text: 'localLanding.faqs.heading',
    },
    {
      anchorId: AnchorLinks.LocationCarousel,
      text: 'localLanding.locationCarousel.title',
    },
    ...(isArticleVisible.value
      ? [
          {
            anchorId: AnchorLinks.Articles,
            text: 'landing.article.title',
          },
        ]
      : []),
  ];
});

const handleClick = (anchorId: string) => {
  trackingGenericClickEvent({
    event: LOCAL_LANDING_PAGE_EVENT.LINK_CLICK,
    category: LOCAL_LANDING_PAGE_CATEGORY.LINK_CLICK,
    action: LOCAL_LANDING_PAGE_ACTION.LLP_CONTENT,
    label: anchorId,
  });
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.container {
  @include grid-media-query(sm) {
    margin-right: auto;
    margin-left: auto;
  }

  > div {
    padding: $space-150;
    border-color: $color-grey-60;
  }

  ul {
    padding-left: $space-150;

    @include vertical-margin;

    li:not(:last-child) {
      margin-bottom: $space-50;
    }

    li::marker {
      font-size: 0.625rem;
      color: $color-azul-110;
    }

    a {
      text-decoration: underline;

      @include body-3($color-azul-110);
    }
  }
}

.heading {
  @include vertical-margin(0, $space-50);
  @include heading-4;
}
</style>
